import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useGroupConfirm, useTerminateAgreement, useToasts } from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'components/types';
import { PATHS } from 'router/config';

import * as L from '../../_detailShared/detail.styled';
import { MoveToGroupInlinePopup } from '../../_detailShared/MoveToGroupInlinePopup';
import {
  formatMoneyCurrency,
  paidPercent,
  progressTone,
} from '../../_detailShared/format';
import { IGroupRosterRow } from './rosterTypes';
import { GroupColors } from './groupColors';

interface IStudentRosterRowProps {
  row: IGroupRosterRow;
  /**
   * Whether the operator may terminate/move this agreement (Req 10.7). When
   * `false` the Move + Terminate controls are hidden; Open stays available.
   */
  canTerminate: boolean;
  /** Refresh the roster after a successful mutation (terminate / move). */
  onChanged: () => void;
}

const fullNameOf = (row: IGroupRosterRow) =>
  [
    row.student.user?.firstName,
    row.student.user?.middleName,
    row.student.user?.lastName,
  ]
    .filter(Boolean)
    .join(' ') || '—';

/**
 * One agreement-as-roster-row inside the GroupDetail Students tab.
 *
 * Each row is an agreement (a student can theoretically appear twice
 * if they have two active agreements in the same group — rare but the
 * data model allows it). Renders:
 *
 *   - identity (full name link → StudentDetail, email/phone),
 *   - agreement chip (title + currency + active/terminated pill),
 *   - per-currency money: budget / paid / debt + a thin progress bar,
 *   - PandaDoc status mini-chip (signed/missing/in-progress),
 *   - actions: Move (inline popup via shared MoveToGroupInlinePopup),
 *     Terminate (one-click via the global `ConfirmPopup` → `useTerminateAgreement`),
 *     Open (router push).
 *
 * Terminate (Req 10.1–10.3): the action no longer opens the generic
 * `EditAgreementPopup`. It opens the shared confirmation modal
 * (`useGroupConfirm`) and, only on confirm, runs the dedicated
 * `terminateAgreement` mutation via `useTerminateAgreement` (just the agreement
 * id — no PII echo). On success the roster is refreshed (`onChanged`); on
 * failure the roster is left unchanged and a localized error toast is shown.
 */
export const StudentRosterRow = memo(
  ({ row, canTerminate, onChanged }: IStudentRosterRowProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();
    const confirm = useGroupConfirm();
    const { terminate, pending } = useTerminateAgreement(onChanged);

    const [moveOpen, setMoveOpen] = useState(false);

    const fullName = useMemo(() => fullNameOf(row), [row]);
    const studentId = row.student.id;
    const email = row.student.user?.email;
    const phone = row.student.user?.phone;

    const budget = Number(row.budgetAmount) || 0;
    const paid = Number(row.paidAmount) || 0;
    const debt = Math.max(0, budget - paid);
    const pct = paidPercent(budget, paid);
    const tone = progressTone(pct);

    // B2: server-provided counts replace the old `contributions.edges.length`.
    // `contributionsCount` is the REMAINING (unclosed) installments; pairing it
    // with `paidContributionsCount` renders progress as «paid / total».
    const remainingContributions = row.contributionsCount ?? 0;
    const paidContributions = row.paidContributionsCount ?? 0;
    const totalContributions = remainingContributions + paidContributions;
    const isTerminated = Boolean(row.terminated);

    const pandaTone: 'ok' | 'warn' | 'danger' | 'neutral' = useMemo(() => {
      const d = row.pandadocDocument;
      if (!d) return 'neutral';
      const status = (d.status || '').toString().toLowerCase();
      const outcome = (d.outcome || '').toString().toLowerCase();
      if (outcome === 'signed' || status.includes('completed')) return 'ok';
      if (outcome === 'lost' || status.includes('cancel')) return 'danger';
      return 'warn';
    }, [row.pandadocDocument]);

    const onOpenStudent = useCallback(() => {
      history.push(PATHS.studentDetail.replace(':id', studentId));
    }, [history, studentId]);

    const onTerminate = useCallback(async () => {
      if (pending) return;

      // B3: the dedicated `terminateAgreement` mutation needs only the agreement
      // id — no PII echo, so the old "missing required data" pre-check is gone.
      const confirmed = await confirm({
        title: t('pages.groupDetail.students.terminate.confirmTitle'),
        message: t('pages.groupDetail.students.terminate.confirmMessage', {
          name: fullName,
        }),
        confirmLabel: t('pages.groupDetail.students.terminate.confirmButton'),
        cancelLabel: t('pages.groupDetail.students.terminate.cancelButton'),
      });
      if (!confirmed) return;

      const { success, alreadyTerminated } = await terminate({
        agreementId: row.id,
      });

      if (!success) {
        addToast({
          data: {
            title: t('pages.groupDetail.students.terminate.errorTitle'),
            text: alreadyTerminated
              ? t('pages.groupDetail.students.terminate.alreadyText', {
                  name: fullName,
                })
              : t('pages.groupDetail.students.terminate.errorText', {
                  name: fullName,
                }),
            type: ToastTypes.error,
          },
          type: GlobalToastTypeNames.StudentAgreementEdit,
        });
      }
    }, [pending, confirm, t, fullName, row.id, terminate, addToast]);

    return (
      <>
        <tr>
          <td>
            <L.ChipLink type="button" onClick={onOpenStudent}>
              {fullName}
            </L.ChipLink>
            <MutedLine>
              {email || '—'}
              {phone ? ` · ${phone}` : ''}
            </MutedLine>
          </td>
          <td>
            <AgreementTitle>{row.agreementTitle || '—'}</AgreementTitle>
            <TagRow>
              <L.Tag tone={isTerminated ? 'danger' : 'ok'}>
                {isTerminated
                  ? t('pages.groupDetail.students.terminated')
                  : t('pages.groupDetail.students.active')}
              </L.Tag>
              <L.Tag tone="neutral">{row.currency || '—'}</L.Tag>
              {row.pandadocDocument ? (
                <L.Tag tone={pandaTone}>
                  {t('pages.groupDetail.students.pandadoc')}
                </L.Tag>
              ) : (
                <L.Tag tone="neutral">
                  {t('pages.groupDetail.students.pandadocMissing')}
                </L.Tag>
              )}
            </TagRow>
          </td>
          <td>
            <div>{formatMoneyCurrency(budget, row.currency)}</div>
            <L.ProgressTrack>
              <L.ProgressFill pct={pct} tone={tone} />
            </L.ProgressTrack>
          </td>
          <PaidCell>{formatMoneyCurrency(paid, row.currency)}</PaidCell>
          <DebtCell hasDebt={debt > 0}>
            {formatMoneyCurrency(debt, row.currency)}
          </DebtCell>
          <CountCell>
            {paidContributions}/{totalContributions}
          </CountCell>
          <ActionsCell>
            {canTerminate ? (
              <>
                <L.PrimaryActionButton
                  type="button"
                  onClick={() => setMoveOpen(true)}
                >
                  {t('pages.groupDetail.students.actions.move')}
                </L.PrimaryActionButton>{' '}
                <L.PrimaryActionButton
                  type="button"
                  disabled={pending}
                  onClick={onTerminate}
                >
                  {t('pages.groupDetail.students.actions.terminate')}
                </L.PrimaryActionButton>{' '}
              </>
            ) : null}
            <L.PrimaryActionButton type="button" onClick={onOpenStudent}>
              {t('pages.groupDetail.students.actions.open')}
            </L.PrimaryActionButton>
          </ActionsCell>
        </tr>
        {moveOpen ? (
          <MoveToGroupInlinePopup
            agreement={{
              id: row.id,
              currency: row.currency,
              agreementTitle: row.agreementTitle,
              // The roster query doesn't include `agreement.group` (we
              // already know it — it's the current group). Pass `null`
              // so the popup just blocks the «same-group» case via the
              // server-side check rather than the client-side hint.
              group: null,
            }}
            studentId={studentId}
            onClose={() => setMoveOpen(false)}
            onMoved={() => {
              setMoveOpen(false);
              onChanged();
            }}
          />
        ) : null}
      </>
    );
  },
);

const MutedLine = styled.div`
  font-size: 11px;
  color: ${GroupColors.textMuted};
  margin-top: 2px;
`;

const AgreementTitle = styled.div`
  font-weight: 600;
  color: ${GroupColors.textPrimary};
`;

const TagRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
`;

const PaidCell = styled.td`
  color: ${GroupColors.success};
  font-weight: 600;
`;

const DebtCell = styled.td<{ hasDebt: boolean }>`
  color: ${({ hasDebt }) =>
    hasDebt ? GroupColors.warn : GroupColors.textMuted};
  font-weight: ${({ hasDebt }) => (hasDebt ? 600 : 400)};
`;

const CountCell = styled.td`
  text-align: center;
  color: ${GroupColors.textSecondary};
`;

const ActionsCell = styled.td`
  white-space: nowrap;
`;
